import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { CloudUpload, WarningAmberOutlined } from '@mui/icons-material';
import Loading from '../../components/loading';
import { useNavigate } from 'react-router-dom';
import { useSaveTerm } from './hooks/useSaveTerm';

const VisuallyHiddenInput = styled('input')({
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
});

function TermsModal({ open, onClose, onSave, currentTerm }) {
  const navigate = useNavigate();
  const { save: saveTermRequest } = useSaveTerm();

  const [isLoading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [showInvalidFileDialog, setShowInvalidFileDialog] = useState(false);

  const isEditing = !!currentTerm;

  useEffect(() => {
    if (open && isEditing) {
      setDescription(currentTerm.description);
    }
    if (!open || !isEditing) {
      setDescription('');
      setFile(null);
    }
    if (!open) {
      setShowInvalidFileDialog(false);
    }
  }, [open, currentTerm, isEditing]);

  const fileChange = (event) => {
    const uploadedFile = event.target.files[0];
    // Alguns navegadores não informam o mime type do .docx, então a extensão
    // também é aceita como confirmação do formato.
    const isDocx =
      !!uploadedFile &&
      (uploadedFile.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        uploadedFile.name.toLowerCase().endsWith('.docx'));

    // Permite selecionar o mesmo arquivo novamente após um erro.
    event.target.value = '';

    if (!uploadedFile) {
      return;
    }

    if (isDocx) {
      setFile(uploadedFile);
      return;
    }

    setFile(null);
    setShowInvalidFileDialog(true);
  };

  const saveTerm = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('description', description);
    if (file) {
      formData.append('file', file);
    }

    saveTermRequest(formData)
      .then((returnFromApi) => {
        onSave(returnFromApi);
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        }
        onSave(returnFromApi);
      });
  };

  const closeModal = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
      <Loading isToHidden={isLoading === false} />

      <DialogTitle>{isEditing ? 'Editar Termo' : 'Cadastrar Novo Termo'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="descricao"
          label="Descrição do Termo"
          type="text"
          fullWidth
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 3, mt: 1 }}
        />

        <Button
          component="label"
          role={undefined}
          variant="outlined"
          tabIndex={-1}
          startIcon={<CloudUpload />}
          fullWidth
        >
          {file
            ? file.name
            : isEditing
              ? 'Substituir Termo (.docx)'
              : 'Fazer Upload do Termo (.docx)'}
          <VisuallyHiddenInput
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={fileChange}
          />
        </Button>
      </DialogContent>
      <Dialog
        open={showInvalidFileDialog}
        onClose={() => setShowInvalidFileDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          <WarningAmberOutlined sx={{ mr: 1, verticalAlign: 'middle' }} color="warning" />
          Formato de arquivo inválido
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Typography gutterBottom>
              O sistema aceita apenas arquivos no formato <strong>.docx</strong> (Word).
            </Typography>
            <Typography gutterBottom>
              Converta o seu arquivo para .docx e faça o upload novamente.
            </Typography>
            <Typography>
              Se preferir, envie o arquivo para{' '}
              <Link href="mailto:jeandonato@dncode.com.br">jeandonato@dncode.com.br</Link> que nós
              convertemos para você.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowInvalidFileDialog(false)}>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <DialogActions>
        <Button onClick={closeModal}>Cancelar</Button>
        <Button
          onClick={saveTerm}
          variant="contained"
          disabled={!description || (!file && !isEditing)}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TermsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentTerm: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    date: PropTypes.string,
  }),
};

TermsModal.defaultProps = {
  currentTerm: null,
};

export default TermsModal;
